import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Avatar,
    Card,
    CardContent,
    CardActions,
    Divider,
    Radio,
    RadioGroup,
    FormControlLabel,
    Alert,
    Chip,
} from '@mui/material';
import {
    CreditCard as CreditCardIcon,
    Delete as DeleteIcon,
    AddCard as AddCardIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import SubscribeButton from '../../SubscribeButton';

export interface PaymentMethod {
    token: string;
    cardType?: string;
    last4?: string;
    expirationDate?: string;
    expirationMonth?: string;
    expirationYear?: string;
    cardholderName?: string | null;
    default?: boolean;
    bin?: string;
    maskedNumber?: string;
    imageUrl?: string;
    createdAt?: string;
    updatedAt?: string;
    expired?: boolean;
    customerId?: string;
    customerLocation?: string;
    issuingBank?: string;
    countryOfIssuance?: string;
    debit?: string;
    prepaid?: string;
    commercial?: string;
    payroll?: string;
    healthcare?: string;
    durbinRegulated?: string;
    paidThroughDate?: string;
    isNetworkTokenized?: boolean;
    venmoSdk?: boolean;
    subscriptions?: any[];
    verifications?: any[];
    uniqueNumberIdentifier?: string;
    globalId?: string;
    graphQLId?: string;
    customerGlobalId?: string;
    productId?: string;
    purchase?: string;
    corporate?: string;
    consumer?: string;
    business?: string;
    prepaidReloadable?: string;
}

interface ManagePaymentMethodModalProps {
    open: boolean;
    onClose: () => void;
    onFinish: (selectedPayment: PaymentMethod | null) => void;
    onDelete: (selectedPayment: PaymentMethod | null) => void;
    paymentMethods?: PaymentMethod[] | null;
    userData: any;
    currentPaymentMethod?: PaymentMethod | null;
    planId?: string;
    subscriptionId?: string;
}

export default function ManagePaymentMethodModal({
    open,
    onClose,
    onFinish,
    onDelete,
    paymentMethods,
    userData,
    currentPaymentMethod,
    planId = '',
    subscriptionId,
}: ManagePaymentMethodModalProps) {
    const [showAddPayment, setShowAddPayment] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState<PaymentMethod | null>(paymentMethods?.find(pm => pm.globalId === currentPaymentMethod?.globalId) || null);
    const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [paymentToDelete, setPaymentToDelete] = useState<PaymentMethod | null>(null);

    useEffect(() => {
        setSelectedPayment(paymentMethods?.find(pm => pm.globalId === currentPaymentMethod?.globalId) || null);
      }, [currentPaymentMethod, paymentMethods]);

    const handleSelectPayment = (globalId: string) => {
        const payment = paymentMethods?.find(pm => pm.globalId === globalId);
        setSelectedPayment(payment || null);
    };

    const handleDeletePayment = (payment: PaymentMethod) => {
        setPaymentToDelete(payment);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = () => {
        onDelete(paymentToDelete);
        setDeleteConfirmOpen(false);
        setPaymentToDelete(null);
    };

    const handleCancelDelete = () => {
        setDeleteConfirmOpen(false);
        setPaymentToDelete(null);
    };

    const handleSavePaymentSelection = async () => {
        onFinish(selectedPayment);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <CreditCardIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            {showAddPayment ? 'Add Payment Method' : 'Manage Payment Methods'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {showAddPayment
                                ? 'Add a new card to your account'
                                : 'View and manage your payment methods'}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent>
                {!showAddPayment ? (
                    <Box sx={{ mt: 2 }}>
                        {/* Existing Payment Methods List */}
                        {paymentMethods && paymentMethods.length > 0 ? (
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                                    Your Payment Methods
                                </Typography>
                                <RadioGroup
                                    value={selectedPayment?.globalId || ''}
                                    onChange={(e) => handleSelectPayment(e.target.value)}
                                >
                                    {paymentMethods.map((method) => (
                                        <Card key={method.token} sx={{ mb: 2, border: 1, borderColor: 'divider' }}>
                                            <CardContent sx={{ pb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <FormControlLabel
                                                        value={method.globalId}
                                                        control={<Radio />}
                                                        label={
                                                            <Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <CreditCardIcon fontSize="small" color="action" />
                                                                    <Typography variant="body2" fontWeight={600}>
                                                                        {method.cardType} ending in {method.last4}
                                                                    </Typography>
                                                                    {method.globalId === currentPaymentMethod?.globalId && (
                                                                        <Chip label="Active" size="small" color="info" sx={{ color: "background.paper"}} />
                                                                    )}
                                                                </Box>
                                                                {method.cardholderName && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {method.cardholderName}
                                                                    </Typography>
                                                                )}
                                                                {method.expirationDate && (
                                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                                        Expires: {method.expirationDate}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                        sx={{ flex: 1, m: 0 }}
                                                    />
                                                </Box>
                                            </CardContent>
                                            <CardActions sx={{ pt: 0, justifyContent: 'flex-end' }}>
                                                {method.globalId !== currentPaymentMethod?.globalId && (
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        startIcon={<DeleteIcon />}
                                                        onClick={() => handleDeletePayment(method)}
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                            </CardActions>
                                        </Card>
                                    ))}
                                </RadioGroup>

                                <Divider sx={{ my: 3 }} />
                            </Box>
                        ) : (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                No payment methods on file. Add one to get started.
                            </Alert>
                        )}

                        {/* Add New Payment Method Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddCardIcon />}
                                onClick={() => setShowAddPayment(true)}
                                fullWidth
                            >
                                Add Payment Method
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ mt: 2 }}>
                        <SubscribeButton
                            planId={planId}
                            userData={userData}
                            setShowAddPaymentDialog={() => setShowAddPayment(false)}
                            paymentMethodLoading={paymentMethodLoading}
                            setPaymentMethodLoading={setPaymentMethodLoading}
                            canCancel={true}
                            onSuccess={(() => {
                                onFinish(selectedPayment)
                            })}
                            subscriptionId={subscriptionId}
                            subscribe={false}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
                {!showAddPayment ? (
                    <>
                        <Button onClick={onClose} variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSavePaymentSelection}
                            variant="contained"
                            color="primary"
                            disabled={selectedPayment?.globalId === currentPaymentMethod?.globalId}
                        >
                            Set as Active Payment Method
                        </Button>
                    </>
                ) : (
                    <Button
                        onClick={() => setShowAddPayment(false)}
                        variant="outlined"
                    >
                        Back
                    </Button>
                )}
            </DialogActions>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'error.main' }}>
                            <WarningIcon />
                        </Avatar>
                        <Typography variant="h6" fontWeight={600}>
                            Delete Payment Method?
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Are you sure you want to delete the <strong>{paymentToDelete?.cardType} ending in {paymentToDelete?.last4}</strong> payment method?
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={handleCancelDelete} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
